import { Button, Stack } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { IconPlus } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import { z } from 'zod/v4';

import {
  type BankAccountType,
  BankAccountTypeSchema,
  type UpdateBankAccountsVariables,
  useLoadBankAccounts,
  useUpdateBankAccounts,
} from '../api';

import { BankAccountRow } from './BankAccountRow';

const BankAccountsFormSchema = z.object({
  companyBankAccounts: z.array(
    z.object({
      id: z.string().optional(),
      iban: z.string().min(1),
      bic: z.string(),
    }),
  ),
  defaultBankAccountId: z.number().nullable(),
  defaultBankAccountType: BankAccountTypeSchema.nullable(),
  archivedCodaboxBankAccountIds: z.array(z.string()),
});

type BankAccountsFormValues = z.infer<typeof BankAccountsFormSchema>;

const INITIAL_VALUES: BankAccountsFormValues = {
  companyBankAccounts: [],
  defaultBankAccountId: null,
  defaultBankAccountType: null,
  archivedCodaboxBankAccountIds: [],
};

const NEW_ACCOUNT = { iban: '', bic: '' } as const;

export const BankAccountsCard = () => {
  const { t } = useTranslation();
  const { bankAccounts, isBankAccountsLoading } = useLoadBankAccounts();
  const { updateBankAccounts, isUpdateBankAccountsLoading } = useUpdateBankAccounts();

  const form = useForm<BankAccountsFormValues>({
    initialValues: INITIAL_VALUES,
    validate: schemaResolver(BankAccountsFormSchema, { sync: true }),
  });

  const resetToLoadedAccounts = () => {
    if (!bankAccounts) return;

    const values: BankAccountsFormValues = {
      companyBankAccounts: bankAccounts.companyBankAccounts.map(({ id, iban, bic }) => ({
        id,
        iban,
        bic: bic ?? '',
      })),
      defaultBankAccountId: bankAccounts.defaultBankAccountId,
      defaultBankAccountType: bankAccounts.defaultBankAccountType,
      archivedCodaboxBankAccountIds: [],
    };
    form.setValues(values);
    form.resetDirty();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync server-loaded accounts into the form only when the fetched data changes, not on every render (the form object identity is unstable)
  useEffect(resetToLoadedAccounts, [bankAccounts]);

  const syncedAccounts = (bankAccounts?.bankAccounts ?? []).filter(
    ({ id }) => !form.getValues().archivedCodaboxBankAccountIds.includes(id),
  );
  const manualAccounts = form.getValues().companyBankAccounts;

  const isDefault = (id: string | undefined, type: BankAccountType) =>
    id !== undefined &&
    form.getValues().defaultBankAccountId === Number(id) &&
    form.getValues().defaultBankAccountType === type;

  const isArchivable = ({ id, type }: { id: string; type: BankAccountType }) =>
    type === 'CodaboxBankAccount' && !isDefault(id, type);

  const setDefault = (id: string, type: BankAccountType) => {
    form.setFieldValue('defaultBankAccountId', Number(id));
    form.setFieldValue('defaultBankAccountType', type);
  };

  const handleSave = () => {
    const values = form.getValues();
    const payload: UpdateBankAccountsVariables = {
      companyBankAccounts: values.companyBankAccounts.map(({ id, iban, bic }) => ({
        ...(id === undefined ? {} : { id }),
        iban,
        bic,
      })),
      defaultBankAccountId: values.defaultBankAccountId,
      defaultBankAccountType: values.defaultBankAccountType,
      archivedCodaboxBankAccountIds: values.archivedCodaboxBankAccountIds,
    };

    updateBankAccounts(payload, {
      onSuccess: () => form.resetDirty(),
      onError: resetToLoadedAccounts,
    });
  };

  return (
    <Panel
      title={t(i18nKeys.COMMON.BANK_ACCOUNT)}
      withBorder
      fullHeight
      pinFooter
      isLoading={isBankAccountsLoading}
      topRight={
        <Button
          variant="light"
          size="xs"
          radius="md"
          leftSection={<IconPlus size={16} />}
          onClick={() => form.insertListItem('companyBankAccounts', { ...NEW_ACCOUNT })}
        >
          {t(i18nKeys.FORM.CLIENT.ADD_BANK_ACCOUNT)}
        </Button>
      }
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!form.isDirty()}
          loading={isUpdateBankAccountsLoading}
          hideIcon
        />
      }
    >
      <Stack my="md">
        {syncedAccounts.map(account => (
          <BankAccountRow
            key={`${account.type}-${account.id}`}
            ibanValue={account.iban}
            bicValue={account.bic ?? ''}
            disabled
            isDefault={isDefault(account.id, account.type)}
            onSetDefault={() => setDefault(account.id, account.type)}
            onArchive={
              isArchivable(account)
                ? () =>
                    form.setFieldValue('archivedCodaboxBankAccountIds', ids => [...ids, account.id])
                : undefined
            }
          />
        ))}
        {manualAccounts.map((account, index) => (
          <BankAccountRow
            key={account.id ?? `new-${index}`}
            ibanValue={account.iban}
            bicValue={account.bic}
            onIbanChange={value => form.setFieldValue(`companyBankAccounts.${index}.iban`, value)}
            onBicChange={value => form.setFieldValue(`companyBankAccounts.${index}.bic`, value)}
            ibanError={form.errors[`companyBankAccounts.${index}.iban`]}
            bicError={form.errors[`companyBankAccounts.${index}.bic`]}
            isDefault={isDefault(account.id, 'CompanyBankAccount')}
            onSetDefault={
              account.id === undefined
                ? undefined
                : () => setDefault(account.id as string, 'CompanyBankAccount')
            }
            onRemove={
              isDefault(account.id, 'CompanyBankAccount')
                ? undefined
                : () => form.removeListItem('companyBankAccounts', index)
            }
          />
        ))}
      </Stack>
    </Panel>
  );
};
